import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Calendar, DollarSign, Package, Plus, MessageSquare, PhoneCall, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agent, setAgent] = useState(null);
  const [client, setClient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('timeline');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCommModal, setShowCommModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    taskType: 'follow_up',
    priority: 'medium',
    dueDate: ''
  });
  const [newComm, setNewComm] = useState({
    communicationType: 'call',
    summary: '',
    outcome: '',
    duration: ''
  });

  useEffect(() => {
    const agentData = localStorage.getItem('agent');
    if (!agentData) {
      navigate('/agent');
      return;
    }
    setAgent(JSON.parse(agentData));
  }, [navigate]);

  useEffect(() => {
    if (agent && clientId) {
      fetchClientData();
    }
  }, [agent, clientId]);

  const fetchClientData = async () => {
    try {
      // Fetch client info
      const clientResponse = await fetch(`${BACKEND_URL}/api/users/${clientId}`);
      if (clientResponse.ok) {
        const clientData = await clientResponse.json();
        setClient(clientData);
      }

      // Fetch timeline
      const timelineResponse = await fetch(`${BACKEND_URL}/api/agent/timeline/${clientId}`);
      if (timelineResponse.ok) {
        const timelineData = await timelineResponse.json();
        setTimeline(timelineData);
      }

      // Fetch notes
      const notesResponse = await fetch(`${BACKEND_URL}/api/agent/notes/${clientId}`);
      if (notesResponse.ok) {
        const notesData = await notesResponse.json();
        setNotes(notesData);
      }

      // Fetch tasks
      const tasksResponse = await fetch(`${BACKEND_URL}/api/agent/tasks/client/${clientId}`);
      if (tasksResponse.ok) {
        const tasksData = await tasksResponse.json();
        setTasks(tasksData);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: clientId,
          agentId: agent.agentId,
          note: newNote,
          attachments: []
        })
      });

      if (response.ok) {
        toast({ title: "Note added successfully" });
        setShowNoteModal(false);
        setNewNote('');
        fetchClientData();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to add note" });
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newTask,
          userId: clientId,
          agentId: agent.agentId
        })
      });

      if (response.ok) {
        toast({ title: "Task created successfully" });
        setShowTaskModal(false);
        setNewTask({ title: '', description: '', taskType: 'follow_up', priority: 'medium', dueDate: '' });
        fetchClientData();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to create task" });
    }
  };

  const handleCreateComm = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/communications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newComm,
          userId: clientId,
          agentId: agent.agentId,
          duration: newComm.duration ? parseInt(newComm.duration) : null
        })
      });

      if (response.ok) {
        toast({ title: "Communication logged successfully" });
        setShowCommModal(false);
        setNewComm({ communicationType: 'call', summary: '', outcome: '', duration: '' });
        fetchClientData();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to log communication" });
    }
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'note': return '📝';
      case 'task': return '✅';
      case 'communication': return '📞';
      case 'payment': return '💰';
      case 'order': return '📦';
      default: return '•';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading || !client) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/agent/panel')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">{client.name}</h1>
                <p className="text-xs text-slate-500">Client ID: #{client.clientId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button size="sm" onClick={() => setShowNoteModal(true)} className="bg-blue-600">
                <Plus className="w-4 h-4 mr-1" />
                Note
              </Button>
              <Button size="sm" onClick={() => setShowTaskModal(true)} className="bg-green-600">
                <Plus className="w-4 h-4 mr-1" />
                Task
              </Button>
              <Button size="sm" onClick={() => setShowCommModal(true)} className="bg-purple-600">
                <Plus className="w-4 h-4 mr-1" />
                Log Call
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Info Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">{client.name}</h2>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                  client.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {client.status}
                </span>
              </div>

              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center space-x-3 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">{client.email}</span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">{client.phone || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">
                    Joined {client.dateJoined ? new Date(client.dateJoined).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <Package className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">Plan: {client.activePlan || 'None'}</span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">LTV: ₹{(client.lifetimeValue || 0).toLocaleString()}</span>
                </div>
              </div>

              <div className="pt-6 border-t">
                <h3 className="font-semibold text-slate-800 mb-3">Quick Stats</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Notes</p>
                    <p className="text-xl font-bold text-slate-800">{notes.length}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Tasks</p>
                    <p className="text-xl font-bold text-slate-800">{tasks.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="border-b border-slate-200 flex">
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`px-6 py-4 font-medium ${
                    activeTab === 'timeline'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  Timeline
                </button>
                <button
                  onClick={() => setActiveTab('notes')}
                  className={`px-6 py-4 font-medium ${
                    activeTab === 'notes'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  Notes ({notes.length})
                </button>
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`px-6 py-4 font-medium ${
                    activeTab === 'tasks'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  Tasks ({tasks.length})
                </button>
              </div>

              <div className="p-6">
                {/* Timeline Tab */}
                {activeTab === 'timeline' && (
                  <div className="space-y-4">
                    {timeline.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No activities yet</p>
                    ) : (
                      timeline.map((item, index) => (
                        <div key={index} className="flex items-start space-x-4 p-4 bg-slate-50 rounded-lg">
                          <div className="text-2xl">{getTimelineIcon(item.type)}</div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{item.title}</p>
                            <p className="text-sm text-slate-600">{item.description}</p>
                            {item.agentName && (
                              <p className="text-xs text-slate-500 mt-1">by {item.agentName}</p>
                            )}
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {formatDate(item.createdAt)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Notes Tab */}
                {activeTab === 'notes' && (
                  <div className="space-y-4">
                    {notes.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No notes yet</p>
                    ) : (
                      notes.map((note) => (
                        <div key={note.noteId} className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-medium text-slate-800">{note.agentName}</p>
                            <span className="text-xs text-slate-500">{formatDate(note.createdAt)}</span>
                          </div>
                          <p className="text-slate-700">{note.note}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Tasks Tab */}
                {activeTab === 'tasks' && (
                  <div className="space-y-4">
                    {tasks.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No tasks yet</p>
                    ) : (
                      tasks.map((task) => (
                        <div key={task.taskId} className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-start space-x-3">
                            {task.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                            ) : (
                              <Clock className="w-5 h-5 text-orange-600 mt-1" />
                            )}
                            <div className="flex-1">
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-sm text-slate-600 mt-1">{task.description}</p>
                              )}
                              <div className="flex items-center space-x-3 mt-2 text-xs text-slate-500">
                                <span className="px-2 py-1 bg-white rounded">{task.taskType}</span>
                                <span className="px-2 py-1 bg-white rounded">{task.priority}</span>
                                {task.dueDate && (
                                  <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">Add Note</h3>
            <form onSubmit={handleCreateNote}>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
                rows="5"
                placeholder="Enter your note..."
                required
              />
              <div className="flex justify-end space-x-3 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowNoteModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600">Add Note</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">Create Task</h3>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <input
                type="text"
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Task title"
                required
              />
              <textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                rows="3"
                placeholder="Description"
              />
              <div className="grid grid-cols-2 gap-4">
                <select
                  value={newTask.taskType}
                  onChange={(e) => setNewTask({...newTask, taskType: e.target.value})}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="follow_up">Follow Up</option>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                </select>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <input
                type="datetime-local"
                value={newTask.dueDate}
                onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              />
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setShowTaskModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600">Create Task</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCommModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">Log Communication</h3>
            <form onSubmit={handleCreateComm} className="space-y-4">
              <select
                value={newComm.communicationType}
                onChange={(e) => setNewComm({...newComm, communicationType: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="call">Phone Call</option>
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="meeting">Meeting</option>
              </select>
              <textarea
                value={newComm.summary}
                onChange={(e) => setNewComm({...newComm, summary: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                rows="3"
                placeholder="Communication summary"
                required
              />
              <input
                type="text"
                value={newComm.outcome}
                onChange={(e) => setNewComm({...newComm, outcome: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Outcome (e.g., Positive, Scheduled follow-up)"
                required
              />
              <input
                type="number"
                value={newComm.duration}
                onChange={(e) => setNewComm({...newComm, duration: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Duration (minutes)"
              />
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setShowCommModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600">Log Communication</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
